import { useState } from 'react';
import styled from 'styled-components';
import { SMALL_MAX } from '@frameio/utils/breakpoints';
import {
  Box,
  Button2,
  Dialog,
  Text,
  theme,
  Toggle,
  ToggleCheckbox,
  ToggleLabel,
} from '@frameio/vapor';
import TEXT from './generated/TEXT/TranscriptionConsentModal';

TEXT.DEFINE({
  /** Title of the modal */
  title: 'Label Speakers',
  /** Body text for the modal - used with single file */
  body_single_file:
    'Frame.io Transcription will process the voices in this file to separate the transcript by speaker (e.g. Speaker 1, Speaker 2). By using this feature, you confirm that the individuals featured in this content provided approval for you to do so.',
  /** Body text for the modal - used with multiple files */
  body_multiple_files:
    'Frame.io Transcription will process the voices in these files to separate the transcript by speaker (e.g. Speaker 1, Speaker 2). By using this feature, you confirm that the individuals featured in this content provided approval for you to do so.',
  /** Disclaimer text */
  disclaimer: 'This feature may not be available in certain locations.',
  /** Consent checkbox text */
  saveForFuture: 'I consent to speaker labeling for all future transcriptions',
  /** Agree button label */
  buttonLabel: 'I Agree',
  /** Cancel button label */
  cancelLabel: 'Cancel',
});

const DIALOG_MAX_WIDTH = 434;
const DIALOG_MIN_HEIGHT = 244;
export const TranscriptionConsentModal = ({
  isShowing,
  numberOfFiles,
  onRequestClose,
  onSubmit,
}: {
  isShowing: boolean;
  numberOfFiles: number;
  onRequestClose: () => void;
  onSubmit: (rememberConsentChoice: boolean) => void;
}) => {
  const [rememberConsentChoice, setRememberConsentChoice] = useState(false);
  return (
    <Dialog
      aria-label={TEXT.title}
      isShowing={isShowing}
      style={{
        width: `min(95vw, ${DIALOG_MAX_WIDTH}px)`,
        maxWidth: `${DIALOG_MAX_WIDTH}px`,
        minHeight: `${DIALOG_MIN_HEIGHT}px`,
      }}
    >
      <Container>
        <Box alignItems="center" display="flex" gap={12} paddingBottom={12}>
          <Text color="primary" fontWeight="semiBold" variant="header">
            {TEXT.title}
          </Text>
        </Box>
        <div>
          <Text color="body" variant="body">
            {numberOfFiles === 1
              ? TEXT.body_single_file
              : TEXT.body_multiple_files}
          </Text>
          <StyledText color="body" variant="body">
            {TEXT.disclaimer}
          </StyledText>
        </div>
        <CheckboxWrapper>
          <Toggle
            checked={rememberConsentChoice}
            onChange={(e) => setRememberConsentChoice(e.target.checked)}
          >
            <Box alignItems="center" display="flex" gap={8}>
              <ToggleCheckbox style={{ flexShrink: '0' }} />
              <CheckboxLabel color="body" variant="body">
                {TEXT.saveForFuture}
              </CheckboxLabel>
            </Box>
          </Toggle>
        </CheckboxWrapper>
        <StyledBox display="flex" gap={12}>
          <StyledButton onPress={onRequestClose} variant="secondary">
            {TEXT.cancelLabel}
          </StyledButton>
          <StyledButton
            data-testid="transcription-consent-modal-submit-button"
            onPress={() => onSubmit(rememberConsentChoice)}
            variant="primary"
          >
            {TEXT.buttonLabel}
          </StyledButton>
        </StyledBox>
      </Container>
    </Dialog>
  );
};
const StyledText = styled(Text)`
  padding-top: 20px;
  @media (max-width: ${SMALL_MAX}px) {
    padding-top: ${theme.spaces[24]};
  }
`;
const StyledBox = styled(Box)`
  padding-top: ${theme.spaces[24]};
  @media (max-width: ${SMALL_MAX}px) {
    padding-top: ${theme.spaces[16]};
  }
`;
const Container = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-height: 90vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: ${theme.spaces[32]};
  text-align: center;
`;
const StyledButton = styled(Button2)`
  width: 91px;
`;
const CheckboxWrapper = styled.span<{
  $disabled?: boolean;
}>`
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  padding: ${theme.spaces[48]} 0 ${theme.spaces[8]};

  @media (max-width: ${SMALL_MAX}px) {
    padding: 28px 0 ${theme.spaces[8]};
  }
`;
const CheckboxLabel = styled(ToggleLabel)`
  text-align: left;
`;
